export interface AppSettings {
  autoMarkReadOnOpen: boolean;
  timezone: string;
}

const DEFAULT_SETTINGS: AppSettings = {
  autoMarkReadOnOpen: false, // 默认不自动标记已读
  timezone: 'Asia/Shanghai', // 默认东8区
};

const SETTINGS_KEY = 'emailcare-settings';

export class SettingsManager {
  private static instance: SettingsManager;
  private settings: AppSettings;
  private listeners: Array<(settings: AppSettings) => void> = [];

  private constructor() {
    this.settings = this.loadSettings();
  }

  public static getInstance(): SettingsManager {
    if (!SettingsManager.instance) {
      SettingsManager.instance = new SettingsManager();
    }
    return SettingsManager.instance;
  }

  private loadSettings(): AppSettings {
    try {
      const savedSettings = localStorage.getItem(SETTINGS_KEY);
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.error('加载设置失败:', error);
    }
    return { ...DEFAULT_SETTINGS };
  }

  public getSettings(): AppSettings {
    return { ...this.settings };
  }

  public getSetting<K extends keyof AppSettings>(key: K): AppSettings[K] {
    return this.settings[key];
  }

  public updateSettings(newSettings: Partial<AppSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
    this.notifyListeners();
  }

  public updateSetting<K extends keyof AppSettings>(key: K, value: AppSettings[K]): void {
    this.settings[key] = value;
    this.saveSettings();
    this.notifyListeners();
  }

  private saveSettings(): void {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.settings));
    } catch (error) {
      console.error('保存设置失败:', error);
    }
  }

  public addListener(listener: (settings: AppSettings) => void): void {
    this.listeners.push(listener);
  }

  public removeListener(listener: (settings: AppSettings) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.getSettings());
      } catch (error) {
        console.error('设置监听器执行失败:', error);
      }
    });
  }

  public resetToDefaults(): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.saveSettings();
    this.notifyListeners();
  }
}

// 导出单例实例
export const settingsManager = SettingsManager.getInstance();

// 导出便捷函数
export const getSettings = () => settingsManager.getSettings();
export const getSetting = <K extends keyof AppSettings>(key: K) => settingsManager.getSetting(key);
export const updateSettings = (settings: Partial<AppSettings>) => settingsManager.updateSettings(settings);
export const updateSetting = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => 
  settingsManager.updateSetting(key, value); 